import { Form } from '@ksyos/design-system';
import { createRef, useState } from 'react';
import type { IOcrkV1DiagnosticReportCustomControlData } from '@common/interfaces/ophthalmology/OorkAndOcrk/ocrkV1DiagnosticReportCustomControlData';
import type { ICustomControlDiagnosticReportAddEditProps } from '@frontend/components/pages/customControls/interfaces';
import { ProcessEntity, saveTypeEnum, ToastType } from '@common/enums/index';
import { getDisplayName } from '@common/utils/getDisplayName';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import { SaveButton } from '@frontend/components/elements/carePathButtons/SaveButton';
import { SaveSendButton } from '@frontend/components/elements/carePathButtons/SaveSendButton';
import { ButtonContainer } from '@frontend/components/elements/common';
import { FormContextProvider } from '@frontend/components/elements/formContext';
import { TextAreaFormGroupField } from '@frontend/components/fields/TextAreaField';
import { translateKey } from '@frontend/translate';
import { showServiceToast } from '@frontend/utils/index';
import { BooleanRadioGroupFormGroupField } from '../../../../fields/BooleanRadioGroupField';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { DiagnosticReportAfwijkingenAddEdit } from '../../OORK/v1/components/DiagnosticReportAfwijkingenAddEdit';
import { defaultBooleanRadioGroupFormGroupFieldProps, defaultTextAreaFormGroupFieldProps } from '../../OORK/v1/types';
import { addFormConversion, addTrackFormSubmit } from '../../utils/formAnalytics';

export function OcrkV1DiagnosticReportAddEdit(props: ICustomControlDiagnosticReportAddEditProps) {
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const customControlData: IOcrkV1DiagnosticReportCustomControlData = props.diagnosticReport.customControlData;
    const formRef = createRef<HTMLFormElement>();
    const saveSendButtonRef = createRef<HTMLButtonElement>();

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.process?.dateSent}
                isFirstItem={props.isFirstItem}
                isItemOpen
                organizationName={props.currentDiagnosticReport?.performerOrganization?.name}
                practitionerName={
                    props.currentDiagnosticReport?.performerPractitioner
                        ? getDisplayName(props.currentDiagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                showProcessIcon={true}
                title={translateKey('diagnosticReport.category_consultReport')}
            />

            <Form
                formRef={formRef}
                id="OcrkV1DiagnosticReportAddEditId"
                name="OcrkV1DiagnosticReportAddEdit"
                submitOnEnter={false}
            >
                <FormContextProvider
                    value={{
                        customControlData: true,
                        onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await props.validateOneCustomControl(key),
                        errors: props.errors,
                        item: props.diagnosticReport.customControlData,
                        placeholderGroupName: 'diagnosticReport',
                    }}
                >
                    <BooleanRadioGroupFormGroupField
                        {...defaultBooleanRadioGroupFormGroupFieldProps}
                        automation="is-grading-accepted"
                        fieldKey="is_grading_accepted"
                        isRequired={true}
                        label="Ben je het eens met de bevindingen van de optometrist?"
                    />

                    {customControlData.is_grading_accepted === false && (
                        <DiagnosticReportAfwijkingenAddEdit
                            customControlData={customControlData}
                            errors={props.errors}
                            handleChangeCustomControl={props.handleChangeCustomControl}
                            isRequired={!customControlData.is_grading_accepted}
                            validateOneCustomControl={props.validateOneCustomControl}
                        />
                    )}

                    <BooleanRadioGroupFormGroupField
                        {...defaultBooleanRadioGroupFormGroupFieldProps}
                        fieldKey="betrokkene_gesproken_per_videogesprek"
                        isRequired={true}
                        label="Betrokkene per videogesprek gesproken"
                        onChange={async (value) => {
                            if (!value) {
                                await props.handleChangeCustomControl({ toelichting_videogesprek: null });
                            }
                            await props.handleChangeCustomControl({ betrokkene_gesproken_per_videogesprek: value });
                        }}
                    />

                    {customControlData.betrokkene_gesproken_per_videogesprek && (
                        <TextAreaFormGroupField
                            {...defaultTextAreaFormGroupFieldProps}
                            fieldKey="toelichting_videogesprek"
                            label="Toelichting videogesprek"
                            placeholder="Toelichting op het videogesprek..."
                        />
                    )}

                    <BooleanRadioGroupFormGroupField
                        {...defaultBooleanRadioGroupFormGroupFieldProps}
                        fieldKey="relevante_comorbiditeit"
                        isRequired={true}
                        label="Relevante comorbiditeit in relatie tot de rijgeschiktheid"
                        onChange={async (value) => {
                            if (!value) {
                                await props.handleChangeCustomControl({ toelichting_relevante_comorbiditeit: null });
                            }
                            await props.handleChangeCustomControl({ relevante_comorbiditeit: value });
                        }}
                    />

                    {customControlData.relevante_comorbiditeit && (
                        <TextAreaFormGroupField
                            {...defaultTextAreaFormGroupFieldProps}
                            fieldKey="toelichting_relevante_comorbiditeit"
                            label="Toelichting relevante comorbiditeit"
                            placeholder="Toelichting relevante comorbiditeit..."
                        />
                    )}

                    <TextAreaFormGroupField
                        {...defaultTextAreaFormGroupFieldProps}
                        fieldKey="toelichting"
                        label="Toelichting"
                        placeholder="Een toelichting, opmerkingen of vragen"
                    />
                </FormContextProvider>

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.diagnosticReport}
                            isExisting={!!props.request.id}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                            toastServiceLabelType="toast"
                        />,
                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            save={async () => await saveDiagnosticReport(saveTypeEnum.draft)}
                        />,
                        <SaveSendButton
                            buttonDisabled={buttonDisabled}
                            label={translateKey('contactsAndTasks.completeTask')}
                            saveSend={async () => await saveDiagnosticReport(saveTypeEnum.final)}
                            saveSendButtonRef={saveSendButtonRef}
                        />,
                    ]}
                />
            </Form>
        </ProcessContainer>
    );

    async function saveDiagnosticReport(saveType: saveTypeEnum) {
        setButtonDisabled(true);
        addTrackFormSubmit(saveSendButtonRef.current);

        const validatedDiagnosticReport = await props.validateDiagnosticReport(saveType);
        if (!validatedDiagnosticReport) {
            setButtonDisabled(false);

            return;
        }
        const diagnosticReportId = await props.onSaveDiagnosticReport(saveType);
        if (saveType === saveTypeEnum.final) {
            addFormConversion(formRef.current);
            showServiceToast(
                ToastType.success,
                'toast.submitSuccessSimple',
                props.service,
                'toast',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'toast',
                translateKey('toast.draftDefaultMessage')
            );
        }
        await props.onSave(diagnosticReportId);
    }
}
